/**
 * PricingCalculatorLayout
 *
 * Two-column layout container for the pricing calculator.
 * - Left column (~65%): Scrollable form content with collapsible sections
 * - Right column (~35%): Sticky summary panel showing real-time totals
 *
 * Mobile: Stacks vertically with summary at bottom.
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorForm.tsx - Main form
 */

import React from 'react';
import {
  Box,
  alpha,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Link,
} from '@mui/material';
import {
  Calculator as CalculatorIcon,
  FileText as FileTextIcon,
  TrendingUp as ScaleIcon,
  ChevronsDown as ExpandAllIcon,
  ChevronsUp as CollapseAllIcon,
  ExternalLink as ExternalLinkIcon,
} from 'lucide-react';

const PRICING_CALCULATOR_USER_GUIDE_URL =
  'https://www.notion.so/mosaic-app/Pricing-Calculator-V2-2d2f8556468c802189c1d5ae4f3a1fe7';

export interface PricingCalculatorLayoutProps {
  /** The preset selector component to render at top */
  presetSelector?: React.ReactNode;
  /** The main form content (left column on desktop) */
  formContent: React.ReactNode;
  /** The condensed Quote Summary card (sticky at top of right column) */
  quoteSummary?: React.ReactNode;
  /** Optional formulas content to show in a tab */
  formulasContent?: React.ReactNode;
  /** Optional pricing scale table content to show in a tab */
  scaleTableContent?: React.ReactNode;
  /** Active tab index (0 = calculator, 1 = formulas, 2 = scale table) */
  activeTab?: number;
  /** Callback when tab changes */
  onTabChange?: (event: React.SyntheticEvent, newValue: number) => void;
  /** Whether all sections are expanded (for toggle button) */
  allExpanded?: boolean;
  /** Callback when expand/collapse toggle is clicked */
  onToggleExpandAll?: () => void;
}

export const PricingCalculatorLayout: React.FC<PricingCalculatorLayoutProps> = ({
  presetSelector,
  formContent,
  quoteSummary,
  formulasContent,
  scaleTableContent,
  activeTab = 0,
  onTabChange,
  allExpanded = true,
  onToggleExpandAll,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isDark = theme.palette.mode === 'dark';

  const renderTabs = () => {
    if ((!formulasContent && !scaleTableContent) || !onTabChange) return null;
    return (
      <Tabs
        value={activeTab}
        onChange={onTabChange}
        sx={{
          minHeight: 40,
          '& .MuiTab-root': {
            minHeight: 40,
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            gap: 1,
            color: isDark ? theme.palette.grey[400] : theme.palette.grey[600],
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        }}
      >
        <Tab icon={<CalculatorIcon size={16} />} iconPosition="start" label="Calculator" />
        {formulasContent && (
          <Tab icon={<FileTextIcon size={16} />} iconPosition="start" label="Pricing Quote" />
        )}
        {scaleTableContent && (
          <Tab icon={<ScaleIcon size={16} />} iconPosition="start" label="Price Scale" />
        )}
      </Tabs>
    );
  };

  // Render expand/collapse button - shown on Calculator and Pricing Quote tabs
  // Tab indices are dynamic based on which optional tabs exist:
  // - Tab 0: Always "Calculator"
  // - Tab 1: "Pricing Quote" (if formulasContent provided) or "Price Scale" (if only scaleTableContent)
  // - Tab 2: "Price Scale" (if both formulasContent and scaleTableContent provided)
  // The expand button should only show on tabs with collapsible content (Calculator and Pricing Quote)
  const renderExpandCollapseButton = () => {
    // Tab 0 is always Calculator - show expand button
    // Tab 1 is Pricing Quote only if formulasContent exists - show expand button
    // Otherwise (Price Scale tab) - don't show expand button
    const isCalculatorTab = activeTab === 0;
    const isPricingQuoteTab = activeTab === 1 && formulasContent;
    if (!isCalculatorTab && !isPricingQuoteTab) return null;
    if (!onToggleExpandAll) return null;
    return (
      <Tooltip title={allExpanded ? 'Collapse all sections' : 'Expand all sections'} arrow>
        <IconButton
          size="small"
          onClick={onToggleExpandAll}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          {allExpanded ? <CollapseAllIcon size={18} /> : <ExpandAllIcon size={18} />}
        </IconButton>
      </Tooltip>
    );
  };

  // The pricing calculator is embedded within the Dashboard layout which has a 64px header
  // We need to account for this when positioning sticky elements
  const dashboardHeaderHeight = 64;

  if (isMobile) {
    // Mobile: Stack vertically
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 64px)', // Account for dashboard header
          backgroundColor: isDark
            ? theme.palette.background.default
            : alpha(theme.palette.grey[100], 0.5),
        }}
      >
        {/* Header Bar with Tabs and Preset Selector */}
        <Box
          sx={{
            position: 'sticky',
            top: dashboardHeaderHeight,
            zIndex: 100,
            backgroundColor: isDark
              ? alpha(theme.palette.background.paper, 0.95)
              : alpha(theme.palette.background.paper, 0.98),
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.1),
            px: 2,
            py: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {renderTabs()}
            {renderExpandCollapseButton()}
          </Box>
          {/* User Guide Link and Preset Selector */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 1,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Link
              href={PRICING_CALCULATOR_USER_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: theme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Pricing Calculator User Guide
              <ExternalLinkIcon size={14} />
            </Link>
            {(activeTab === 0 || activeTab === 1) && presetSelector}
          </Box>
        </Box>

        {/* Content based on tab - dynamic indices: 0=calculator, 1=formulas (if exists), last=scale table (if exists) */}
        {activeTab === 0 ? (
          <>
            <Box
              sx={{
                flex: 1,
                px: 2,
                py: 3,
              }}
            >
              {formContent}
            </Box>

            {/* Summary Panel */}
            <Box>{quoteSummary}</Box>
          </>
        ) : activeTab === 1 && formulasContent ? (
          <Box
            sx={{
              flex: 1,
              px: 2,
              py: 3,
            }}
          >
            {formulasContent}
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              px: 2,
              py: 3,
            }}
          >
            {scaleTableContent}
          </Box>
        )}
      </Box>
    );
  }

  // Desktop: Two-column layout with fixed header and summary, scrollable form

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${dashboardHeaderHeight}px)`, // Fixed height container
        overflow: 'hidden', // Prevent page-level scrolling
        backgroundColor: isDark
          ? theme.palette.background.default
          : alpha(theme.palette.grey[100], 0.5),
      }}
    >
      {/* Header Bar with Tabs and Preset Selector - Fixed at top */}
      <Box
        sx={{
          flexShrink: 0,
          zIndex: 100,
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.95)
            : alpha(theme.palette.background.paper, 0.98),
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1),
          px: 3,
          py: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: '1600px',
            mx: 'auto',
            width: '100%',
          }}
        >
          {renderTabs()}
          {/* User Guide Link and Preset Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link
              href={PRICING_CALCULATOR_USER_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: theme.palette.primary.main,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Pricing Calculator User Guide
              <ExternalLinkIcon size={14} />
            </Link>
            {(activeTab === 0 || activeTab === 1) && presetSelector}
            {renderExpandCollapseButton()}
          </Box>
        </Box>
      </Box>

      {/* Content based on active tab - 0=calculator, 1=formulas (if exists), 2=scale table (if exists) */}
      {activeTab === 0 ? (
        /* Two-Column Content for Calculator */
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            gap: 3,
            px: 3,
            py: 3,
            maxWidth: '1600px',
            mx: 'auto',
            width: '100%',
            overflow: 'hidden', // Contain scrolling to children
          }}
        >
          {/* Left Column: Form Content (scrollable) */}
          <Box
            sx={{
              flex: '1 1 65%',
              minWidth: 0, // Prevents flex item from overflowing
              overflowY: 'auto', // Scroll only this column
              pr: 1, // Add padding for scrollbar
            }}
          >
            {formContent}
          </Box>

          {/* Right Column: Summary Panel (Fixed) */}
          <Box
            sx={{
              flex: '0 0 35%',
              maxWidth: '450px',
              minWidth: '320px',
              overflowY: 'auto', // Allow scroll if content is too tall
            }}
          >
            {quoteSummary}
          </Box>
        </Box>
      ) : activeTab === 1 && formulasContent ? (
        /* Full-width Content for Pricing Quote tab (scrollable) */
        <Box
          sx={{
            flex: 1,
            px: 3,
            py: 3,
            maxWidth: '1200px',
            mx: 'auto',
            width: '100%',
            overflowY: 'auto', // Scroll the content
          }}
        >
          {formulasContent}
        </Box>
      ) : (
        /* Full-width Content for Scale Table (scrollable) */
        <Box
          sx={{
            flex: 1,
            px: 3,
            py: 3,
            maxWidth: '1400px',
            mx: 'auto',
            width: '100%',
            overflowY: 'auto', // Scroll the content
          }}
        >
          {scaleTableContent}
        </Box>
      )}
    </Box>
  );
};

export default PricingCalculatorLayout;
